/**
 * One expert affordance per issue.
 *
 * The drawer already offers "Request expert fix" in four places — the
 * review-only block, the 3-failed-generation-attempts block, a hard failure
 * with no recovery, and the universal fallback (GH #614) — and every one of
 * them files the same ticket. A second, always-visible "Get expert help" next
 * to any of those asks the operator to choose between two doors to the same
 * room.
 *
 * The two are not redundant in purpose: "Request expert fix" is the internal
 * "this needs a person" handoff, while "Get expert help" is the CLIENT-facing
 * opt-in that carries the consent wording. But only ONE should ever be on
 * screen, so "Get expert help" fills the gap the others leave.
 */
export interface ExpertHelpVisibilityInput {
	hasIssue: boolean;
	/** Any of the drawer's existing "Request expert fix" buttons is rendered. */
	hasExistingExpertAction: boolean;
	/** Already handed off — there is nothing left to request. */
	isManualRequested: boolean;
	isApplied: boolean;
	isVerified: boolean;
	/** Rejected or skipped: the operator has decided. */
	isTerminalReviewStatus: boolean;
	status?: string | null;
}

/** Statuses where the issue is settled and help would be meaningless. */
const SETTLED_STATUSES = new Set(["obsolete", "protected"]);

export function shouldShowExpertHelp(input: ExpertHelpVisibilityInput): boolean {
	if (!input.hasIssue) return false;
	if (input.hasExistingExpertAction) return false;
	if (input.isManualRequested) return false;
	if (input.isApplied || input.isVerified) return false;
	if (input.isTerminalReviewStatus) return false;
	if (input.status && SETTLED_STATUSES.has(input.status)) return false;
	return true;
}
